import { AxiosRequestConfig } from "axios";
import {
  API_TOTALREWARD_SERVICE,
  getFileNameFromResponse,
  getServiceHost,
  saveFile,
} from "src/utils/Api";
import ApiClient from "src/utils/ApiClient";
import { serialize } from "src/utils/ObjectToFormData";
import {
  TotalRewardsPerk,
  TotalRewardsPerkCategory,
} from "src/Pages/Company/TotalReward/models/TotalRewardsPerk.models";
import {
  TotalRewardsConfig,
  TotalRewardsConfigDictionary,
} from "src/Pages/Company/TotalReward/models/TotalRewardsConfig.models";

type RequestConfig = Pick<AxiosRequestConfig, "signal">;

interface HydraRows<T> {
  member: T[];
  totalItems: number;
}

const TRSApiClient = new ApiClient({
  baseURL: getServiceHost(API_TOTALREWARD_SERVICE),
  headers: {},
}).client;

TRSApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      error?.response?.status === 500 ||
      error?.response?.data?.status === "fatal"
    ) {
      return Promise.reject(
        new Error("Wystąpił nieoczekiwany błąd. Spróbuj ponownie później.")
      );
    }
    return Promise.reject(error);
  }
);

export const fetchPerks = async (
  params: AxiosRequestConfig["params"],
  { signal }: RequestConfig
): Promise<{ member: TotalRewardsPerk[]; totalItems: number }> => {
  const { data } = await TRSApiClient.get(`/omb/perks`, {
    params,
    signal,
    headers: {
      accept: "application/ld+json",
    },
  });
  return data;
};

export const fetchPerk = async (
  perkId: string,
  { signal }: RequestConfig
): Promise<TotalRewardsPerk> => {
  const { data } = await TRSApiClient.get(`/omb/perks/${perkId}`, {
    signal,
  });
  return data;
};

export const savePerk = async (
  perkId: string | undefined,
  formData: TotalRewardsPerk
) => {
  const { data } = await TRSApiClient.request({
    method: "POST",
    url: `/omb/perks${perkId ? `/${perkId}` : ""}`,
    data: serialize(formData, {
      booleansAsIntegers: true,
      indices: true,
      allowEmptyArrays: false,
    }),
    params: {
      tenant: formData.tenant,
    },
  });
  return data;
};

export const deletePerk = async (id: string, tenant: string) => {
  const { data } = await TRSApiClient.delete(`/omb/perks/${id}`, {
    params: {
      tenant,
    },
  });
  return data;
};

export const deletePerkUsers = async (
  id: string[],
  companyId: string
): Promise<{ message: string }> => {
  const { data } = await TRSApiClient.post(`/omb/perk-users/bulk-delete`, id, {
    params: {
      tenant: companyId,
    },
  });
  return data;
};

export const exportPerkUsers = async (payload: unknown): Promise<undefined> => {
  const response = await TRSApiClient.post(
    `/omb/perk-users/export-extended`,
    payload,
    {
      responseType: "blob",
      timeout: 30000,
    }
  );
  const resultFileName =
    getFileNameFromResponse(response) || `export_total_reward_statement.xlsx`;

  saveFile(response.data, false, resultFileName);
};

export const fetchPerkUsersPeriods = async (
  params: AxiosRequestConfig["params"],
  { signal }: RequestConfig
): Promise<{ period: string }[]> => {
  const { data } = await TRSApiClient.get(`/omb/perk-users/periods`, {
    params,
    signal,
  });
  return data;
};

export const fetchPerksCategories = async (
  params: AxiosRequestConfig["params"],
  { signal }: RequestConfig
): Promise<TotalRewardsPerkCategory[]> => {
  const { data } = await TRSApiClient.get(`/omb/perk-categories`, {
    params,
    signal,
  });
  return data;
};

export const fetchPerkCategory = async (
  perkCategoryId: string,
  { signal }: RequestConfig
): Promise<TotalRewardsPerkCategory> => {
  const { data } = await TRSApiClient.get(
    `/omb/perk-categories/${perkCategoryId}`,
    {
      signal,
    }
  );
  return data;
};

export const savePerkCategory = async (
  perkId: string | undefined,
  formData: TotalRewardsPerkCategory,
  tenant: string
) => {
  const { data } = await TRSApiClient.request({
    method: perkId ? "PUT" : "POST",
    url: `/omb/perk-categories${perkId ? `/${perkId}` : ""}`,
    data: formData,
    params: {
      tenant,
    },
  });
  return data;
};

export const fetchConfigsDictionary = async (
  tenant: string,
  { signal }: RequestConfig
): Promise<TotalRewardsConfigDictionary[]> => {
  const { data } = await TRSApiClient.get(`/omb/config/dictionary`, {
    params: {
      tenant,
    },
    signal,
  });
  return data;
};

export const fetchConfigs = async (
  tenant: string,
  { signal }: RequestConfig
): Promise<TotalRewardsConfig[]> => {
  const { data } = await TRSApiClient.get(`/omb/configs`, {
    params: {
      tenant,
    },
    signal,
  });
  return data;
};

export const fetchConfig = async (
  id: string,
  tenant: string,
  { signal }: RequestConfig
): Promise<unknown> => {
  const { data } = await TRSApiClient.get(`/omb/configs/${id}`, {
    params: {
      tenant,
    },
    signal,
  });
  return data;
};

export const createConfig = async (
  payload: TotalRewardsConfig,
  tenant: string
): Promise<unknown> => {
  const { data } = await TRSApiClient.post(`/omb/configs`, {
    ...payload,
    tenant,
  });
  return data;
};

export const editConfig = async (
  id: string,
  payload: TotalRewardsConfig,
  tenant: string
): Promise<TotalRewardsConfig> => {
  const { data } = await TRSApiClient.patch(`/omb/configs/${id}`, payload, {
    headers: {
      "Content-Type": "application/merge-patch+json",
    },
    params: {
      tenant,
    },
  });
  return data;
};

export const deleteConfig = async (
  id: string,
  tenant: string
): Promise<unknown> => {
  const { data } = await TRSApiClient.delete(`/omb/configs/${id}`, {
    params: {
      tenant,
    },
  });
  return data;
};
